import React, { useState } from 'react';
import { CountyOption } from 'interfaces/StateMetadata';
import { SearchResult } from 'interfaces/SearchTypes';
import ReactGA from 'react-ga4';
import MiniStateIcon, { IconSize } from './MiniStateIcon';

interface CountyResultsProps {
  searchResults: SearchResult[];
  selectedState: string | null;
  counties: CountyOption[];
  onClear: () => void;
  onStateSelect: (stateName: string) => void;
  onStateFocus?: (stateName: string | null) => void;
  isTouchDevice: boolean;
  stateMetadata: { [stateName: string]: any };
  navigationContext?: 'search' | 'results';
}

const handleGAEvent = (url: string, label: string) => {
  ReactGA.event({
    category: 'hyperlink',
    action: 'click - ballottrax finder',
    label: `${label}: ${url}`,
  });
};

const CountyResults = ({
  searchResults,
  selectedState,
  counties,
  onClear,
  onStateSelect,
  onStateFocus,
  isTouchDevice,
  stateMetadata,
  navigationContext = 'search',
}: CountyResultsProps) => {
  // Determine what to show - these should be mutually exclusive
  const showingSelectedState = selectedState !== null;
  const showingSearchResults =
    searchResults.length > 0 && !showingSelectedState;
  const noResults = !showingSearchResults && !showingSelectedState;

  // Counties in a state, matched on the canonical display name that county_state
  // carries - not the metadata key, which is lowercased and underscored.
  const countiesInState = (canonicalStateName: string) =>
    counties.filter((county) =>
      county.county_state.endsWith(`, ${canonicalStateName}`),
    );

  const selectedStateCounties = selectedState
    ? countiesInState(stateMetadata[selectedState]?.state || selectedState)
    : [];

  // State for tracking which state groups are expanded
  const [expandedStates, setExpandedStates] = useState<Set<string>>(new Set());

  // Helper function to get back button text
  const getBackButtonText = (): string => {
    return selectedState && navigationContext === 'results'
      ? 'Back to Results'
      : 'Back to Search';
  };

  // Helper function to get status message for selected state
  const getSelectedStateStatus = (): string => {
    if (!selectedState) return '';

    const stateName = stateMetadata[selectedState]?.state || selectedState;
    const metadata = stateMetadata[selectedState];
    let supportType: string;

    if (metadata?.link_counties) {
      supportType = `${selectedStateCounties.length} counties available`;
    } else {
      supportType = 'State-wide support available';
    }

    return `Showing results for ${stateName}: ${supportType}`;
  };

  const toggleStateExpansion = (stateName: string) => {
    setExpandedStates((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stateName)) {
        newSet.delete(stateName);
      } else {
        newSet.add(stateName);
      }
      return newSet;
    });
  };

  const INITIAL_COUNTIES_SHOWN = 3;

  // Group counties by state for better organization
  const groupCountiesByState = () => {
    const countyResults = searchResults.filter((r) => r.type === 'county');
    const grouped: { [stateName: string]: SearchResult[] } = {};

    countyResults.forEach((result) => {
      const stateName = result.stateName!;
      if (!grouped[stateName]) {
        grouped[stateName] = [];
      }
      grouped[stateName].push(result);
    });

    // Sort states alphabetically and sort counties within each state
    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([stateName, counties]) => ({
        stateName,
        counties: counties.sort((a, b) =>
          a.county!.county_state.localeCompare(b.county!.county_state),
        ),
      }));
  };

  const renderCountyLink = (county: CountyOption, isFromSearch = false) => {
    const url = county.url || 'https://ballottrax.net/voter/';
    const countyName = county.county_state.split(', ')[0];
    const stateName = county.county_state.split(', ')[1];

    // Find the state key that matches this state name for map highlighting
    const stateKey = Object.keys(stateMetadata).find(
      (key) => stateMetadata[key].state === stateName,
    );

    return (
      <div
        key={county.county_state}
        className={`county-link-card ${isTouchDevice ? 'touch-device' : ''}`}
      >
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            handleGAEvent(url, isFromSearch ? 'search-result' : 'state-browse')
          }
          aria-label={`Visit BallotTrax for ${countyName}, ${stateName} - Opens in new tab`}
          className="county-link"
          onFocus={() => {
            // Update map highlighting when county is focused
            if (onStateFocus && stateKey) {
              onStateFocus(stateKey);
            }
          }}
          onBlur={() => {
            // Clear map highlighting when county loses focus
            if (onStateFocus) {
              onStateFocus(null);
            }
          }}
        >
          <div className="county-info">
            <div className="county-name">{countyName}</div>
            <div className="county-state-label">{stateName}</div>
          </div>
          <div className="county-cta-badge">Visit ↗</div>
        </a>
      </div>
    );
  };

  const [focusedStateCard, setFocusedStateCard] = useState<string | null>(null);

  const isStateResultVisible = (result: SearchResult): boolean => {
    if (result.type !== 'state') return false;
    const state = result.state!;
    const stateCounties = countiesInState(state.state);
    // Hide states with county-only support but no counties available
    return !(state.link_counties && stateCounties.length === 0);
  };

  const renderStateResult = (result: SearchResult) => {
    const state = result.state!;
    const stateCounties = countiesInState(state.state);

    // Hide states with county-only support but no counties available
    if (state.link_counties && stateCounties.length === 0) {
      return null;
    }

    // Helper function to get the county count text
    const getCountyCountText = (
      count: number,
      linkCounties: boolean,
    ): string => {
      // If link_counties is true, it's county-only support
      if (linkCounties) {
        const countyText = count === 1 ? 'county' : 'counties';
        return `${count} ${countyText} available`;
      }
      // If link_counties is false and there's a URL, it's state-wide support
      return 'State-wide support';
    };

    // A state result already carries the metadata key the map highlights by
    const stateKey = result.stateName;

    const isFocused = focusedStateCard === result.stateName;

    // For states with a URL, wrap the entire card in an anchor tag
    if (state.url && !state.link_counties) {
      return (
        <a
          key={result.stateName}
          href={state.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => handleGAEvent(state.url!, 'state-search-result')}
          aria-label={`Visit BallotTrax for ${state.state} - Opens in new tab`}
          className={`state-result-card-new state-result-card-link ${
            isFocused ? 'focused' : ''
          }`}
          onFocusCapture={() => {
            setFocusedStateCard(result.stateName!);
          }}
          onBlurCapture={(e) => {
            // Only remove highlight if focus is leaving the entire card
            const currentTarget = e.currentTarget;
            setTimeout(() => {
              if (!currentTarget.contains(document.activeElement)) {
                setFocusedStateCard(null);
              }
            }, 0);
          }}
        >
          <div className="state-result-header-new">
            <div>
              <h3 className="state-result-title-new">
                <MiniStateIcon stateName={state.state} size={IconSize.MEDIUM} />
                {state.state}
              </h3>
              <div className="state-result-subtitle-new">
                {getCountyCountText(stateCounties.length, state.link_counties)}
              </div>
            </div>
            <div className="state-result-action-new">Visit BallotTrax ↗</div>
          </div>
        </a>
      );
    }

    return (
      <div
        key={result.stateName}
        className={`state-result-card-new ${isFocused ? 'focused' : ''}`}
        onFocusCapture={() => {
          setFocusedStateCard(result.stateName!);
        }}
        onBlurCapture={(e) => {
          // Only remove highlight if focus is leaving the entire card
          const currentTarget = e.currentTarget;
          setTimeout(() => {
            if (!currentTarget.contains(document.activeElement)) {
              setFocusedStateCard(null);
            }
          }, 0);
        }}
      >
        <div className="state-result-header-new">
          <div>
            <h3 className="state-result-title-new">
              <MiniStateIcon stateName={state.state} size={IconSize.MEDIUM} />
              {state.state}
            </h3>
            <div className="state-result-subtitle-new">
              {getCountyCountText(stateCounties.length, state.link_counties)}
            </div>
          </div>

          {stateCounties.length > 0 ? (
            <button
              onClick={() => onStateSelect(result.stateName!)}
              aria-label={`View counties for ${state.state}`}
              className="state-result-action-new"
              onFocus={() => {
                // Update map highlighting when state button is focused
                if (onStateFocus && stateKey) {
                  onStateFocus(stateKey);
                }
              }}
              onBlur={() => {
                // Clear map highlighting when state button loses focus
                if (onStateFocus) {
                  onStateFocus(null);
                }
              }}
            >
              View Counties
            </button>
          ) : state.url ? (
            <a
              href={state.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleGAEvent(state.url!, 'state-search-result')}
              aria-label={`Visit BallotTrax for ${state.state} - Opens in new tab`}
              className="state-result-action-new"
              onFocus={() => {
                // Update map highlighting when state link is focused
                if (onStateFocus && stateKey) {
                  onStateFocus(stateKey);
                }
              }}
              onBlur={() => {
                // Clear map highlighting when state link loses focus
                if (onStateFocus) {
                  onStateFocus(null);
                }
              }}
            >
              Visit BallotTrax ↗
            </a>
          ) : (
            // For states with no action button, make the whole card focusable
            <div
              tabIndex={0}
              role="button"
              aria-label={`${state.state} - ${getCountyCountText(
                stateCounties.length,
                state.link_counties,
              )}`}
              className="state-result-info-new"
              onFocus={() => {
                // Update map highlighting when state info is focused
                if (onStateFocus && stateKey) {
                  onStateFocus(stateKey);
                }
              }}
              onBlur={() => {
                // Clear map highlighting when state info loses focus
                if (onStateFocus) {
                  onStateFocus(null);
                }
              }}
            >
              {state.link_counties
                ? 'County Support Only'
                : 'State-wide Support'}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderGroupedCounties = () => {
    const groupedStates = groupCountiesByState();

    if (groupedStates.length === 0) return null;

    return (
      <>
        <h3 id="county-results-heading" className="county-results-heading">
          Counties ({searchResults.filter((r) => r.type === 'county').length}{' '}
          found)
        </h3>

        {groupedStates.map(({ stateName, counties }) => {
          // Find the state key for this state name
          const stateKey = Object.keys(stateMetadata).find(
            (key) => stateMetadata[key].state === stateName,
          );

          const isExpanded = expandedStates.has(stateName);
          const shouldShowToggle = counties.length > INITIAL_COUNTIES_SHOWN;
          const countiesToShow = isExpanded
            ? counties
            : counties.slice(0, INITIAL_COUNTIES_SHOWN);
          const hiddenCount = counties.length - INITIAL_COUNTIES_SHOWN;

          return (
            <div key={stateName} className="county-group-card">
              <div className="county-group-header">
                <h4 className="county-group-title">
                  <MiniStateIcon stateName={stateName} size={IconSize.SMALL} />
                  {stateName} ({counties.length}{' '}
                  {counties.length === 1 ? 'county' : 'counties'})
                </h4>

                {/* View state button if there are multiple counties and not all are shown */}
                {counties.length > INITIAL_COUNTIES_SHOWN && !isExpanded && (
                  <button
                    onClick={() => toggleStateExpansion(stateName)}
                    className="view-all-button"
                  >
                    View All
                  </button>
                )}
              </div>

              <div role="list" className="county-group-grid">
                {countiesToShow.map((result) => (
                  <div key={result.county!.county_state} role="listitem">
                    {renderCountyLink(result.county!, true)}
                  </div>
                ))}
              </div>

              {/* Show more/less button */}
              {shouldShowToggle && (
                <div className="county-group-toggle-container">
                  <button
                    onClick={() => toggleStateExpansion(stateName)}
                    className="county-group-toggle"
                  >
                    {isExpanded ? `Show Less ↑` : `Show ${hiddenCount} More ↓`}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  };

  if (noResults) {
    return (
      <div className="no-results-container">
        <h3 className="no-results-heading">No results found</h3>
        <p className="no-results-text">
          Try searching for a state name, abbreviation, or county name
        </p>
        <button onClick={onClear} className="browse-all-button">
          Browse All States
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Screen reader announcements */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {showingSearchResults &&
          `Search results: ${
            searchResults.filter(isStateResultVisible).length
          } states and ${
            searchResults.filter((r) => r.type === 'county').length
          } counties found`}
        {showingSelectedState && selectedState && getSelectedStateStatus()}
      </div>

      {/* Context-aware back button */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button onClick={onClear} className="back-button-enhanced">
          ← {getBackButtonText()}
        </button>
      </div>

      {/* Search results */}
      {showingSearchResults && (
        <div role="region" aria-labelledby="search-results-heading">
          <h2 id="search-results-heading" className="results-section-heading">
            Search Results
          </h2>

          {/* State results */}
          {searchResults
            .filter((r) => r.type === 'state')
            .map(renderStateResult)}

          {renderGroupedCounties()}
        </div>
      )}

      {/* Selected state results */}
      {showingSelectedState && selectedState && (
        <div role="region" aria-labelledby="selected-state-heading">
          {stateMetadata[selectedState]?.link_counties &&
          selectedStateCounties.length > 0 ? (
            // County-only support - show counties
            <>
              <h2
                id="selected-state-heading"
                className="results-section-heading"
              >
                {stateMetadata[selectedState]?.state || selectedState} Counties
              </h2>

              <div
                role="list"
                aria-labelledby="selected-state-heading"
                className="county-grid"
              >
                {selectedStateCounties.map((county) => (
                  <div key={county.county_state} role="listitem">
                    {renderCountyLink(county, false)}
                  </div>
                ))}
              </div>
            </>
          ) : !stateMetadata[selectedState]?.link_counties ? (
            // State-wide support - show similar card to search results
            stateMetadata[selectedState]?.url ? (
              <a
                href={stateMetadata[selectedState].url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  handleGAEvent(
                    stateMetadata[selectedState]!.url,
                    'state-map-selection',
                  )
                }
                aria-label={`Visit BallotTrax for ${
                  stateMetadata[selectedState]?.state || selectedState
                } - Opens in new tab`}
                className="state-result-card-new state-result-card-link"
              >
                <div className="state-result-header-new">
                  <div>
                    <h3 className="state-result-title-new">
                      <MiniStateIcon
                        stateName={stateMetadata[selectedState].state}
                        size={IconSize.MEDIUM}
                      />
                      {stateMetadata[selectedState].state}
                    </h3>
                    <div className="state-result-subtitle-new">
                      State-wide support
                    </div>
                  </div>
                  <div className="state-result-action-new">
                    Visit BallotTrax ↗
                  </div>
                </div>
              </a>
            ) : (
              <div className="state-result-card-new">
                <div className="state-result-header-new">
                  <div>
                    <h3 className="state-result-title-new">
                      <MiniStateIcon
                        stateName={stateMetadata[selectedState].state}
                        size={IconSize.MEDIUM}
                      />
                      {stateMetadata[selectedState].state}
                    </h3>
                    <div className="state-result-subtitle-new">
                      State-wide support
                    </div>
                  </div>
                </div>
              </div>
            )
          ) : (
            // County-only support but no counties available
            <>
              <h2
                id="selected-state-heading"
                className="results-section-heading"
              >
                {stateMetadata[selectedState]?.state || selectedState}
              </h2>
              <div className="no-counties-message">
                <p>
                  No counties currently available for{' '}
                  {stateMetadata[selectedState]?.state || selectedState}.
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CountyResults;
